import { Attachment, IAttachment } from '@app/models/Attachment'
import { AttachmentType, ImageSize } from '@app/models/enums'
import {
  FileUploadAttachment,
  IFileUploadAttachment,
} from '@app/models/FileUploadAttachment'
import { ApplicationState } from '@app/store/index'
import { errorToast, successToast } from '@app/utils/notifications'
import {
  createUploadFileChannel,
  makeAuthenticatedRequest,
  RequestMethod,
} from '@app/utils/request'
import { List, Map, Record } from 'immutable'
import { Action, Reducer } from 'redux'
import {
  call,
  fork,
  put,
  select,
  take,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects'
import { createSelector } from 'reselect'
import { v4 as uuid } from 'uuid'

export const MAX_FILE_UPLOAD_SIZE = 125000000

// -----------------
// STATE - This defines the type of data maintained in the Redux store.
export interface IFileUploadState {
  currentFile: IFileUploadAttachment
  files: Map<string, Map<string, IFileUploadAttachment>>
  pendingFileCount: number
  filesPendingUpload: List<IFileUploadAttachment>
  isFileUploadModalOpen: any
  isInEditMode: boolean
}

// initial state
const FileUploadStateRecord = Record({
  currentFile: undefined,
  files: Map<string, Map<string, FileUploadAttachment>>(),
  filesPendingUpload: List<FileUploadAttachment>(),
  isFileUploadModalOpen: {},
  isInEditMode: false,
  pendingFileCount: 0,
})

export class FileUploadState extends FileUploadStateRecord {
  public static fromJS(initialState?: Partial<IFileUploadState>) {
    return new FileUploadState(initialState)
  }

  public readonly currentFile: FileUploadAttachment
  public readonly files: Map<string, Map<string, FileUploadAttachment>>
  public readonly pendingFileCount: number
  public readonly filesPendingUpload: List<FileUploadAttachment>
  public readonly isFileUploadModalOpen: any
  public readonly isInEditMode: boolean

  constructor(props?: Partial<IFileUploadState>) {
    props ? super(props) : super()
  }
}

// -----------------
// ACTIONS - These are serializable (hence replayable) descriptions of state transitions.
// They do not themselves have any side-effects; they just describe something that is going to happen.
// Use @typeName and isActionType for type detection that works even after serialization/deserialization.
export enum FileUploadActionTypes {
  CancelAllFiles = 'FILE_UPLOAD::CANCEL_ALL_FILES',
  CancelCurrentFile = 'FILE_UPLOAD::CANCEL_CURRENT_FILE',
  ClearFileList = 'FILE_UPLOAD::CLEAR_FILE_LIST',
  EditFileDescription = 'FILE_UPLOAD::EDIT_FILE_DESCRIPTION',
  EditFileTitle = 'FILE_UPLOAD::EDIT_FILE_TITLE',
  EditMakePublic = 'FILE_UPLOAD::EDIT_MAKE_PUBLIC',
  EditTagList = 'FILE_UPLOAD::EDIT_TAG_LIST',
  EnableEditFileItem = 'FILE_UPLOAD::ENABLE_EDIT_FILE_ITEM',
  FileUploadRequest = 'FILE_UPLOAD::FILE_UPLOAD_REQUEST',
  FileUploadProgress = 'FILE_UPLOAD::FILE_UPLOAD_PROGRESS',
  FileUploadSuccess = 'FILE_UPLOAD::FILE_UPLOAD_SUCCESS',
  FileUploadFailure = 'FILE_UPLOAD::FILE_UPLOAD_FAILURE',
  LoadExistingAttachments = 'FILE_UPLOAD::LOAD_EXISTING_ATTACHMENTS',
  RemoveFileUploadItem = 'FILE_UPLOAD::REMOVE_FILE_UPLOAD_ITEM',
  ToggleConfirmDeleteModal = 'FILE_UPLOAD::TOGGLE_CONFIRM_DELETE_MODAL',
  ToggleFileUploadModal = 'FILE_UPLOAD::TOGGLE_FILE_UPLOAD_MODAL',
  UpdateFileMetadata = 'FILE_UPLOAD::UPDATE_FILE_METADATA',
}

interface ICancelAllFilesAction {
  type: FileUploadActionTypes.CancelAllFiles
}
interface ICancelCurrentFileAction {
  type: FileUploadActionTypes.CancelCurrentFile
}
interface IClearFileListAction {
  type: FileUploadActionTypes.ClearFileList
  key: string
}
interface IEditFileDescriptionAction {
  type: FileUploadActionTypes.EditFileDescription
  description: string
}
interface IEditFileTitleAction {
  type: FileUploadActionTypes.EditFileTitle
  title: string
}
interface IEditMakePublicAction {
  type: FileUploadActionTypes.EditMakePublic
  value: boolean
}
interface IEditTagListAction {
  type: FileUploadActionTypes.EditTagList
  tags: List<string>
}
interface IEnableEditFileItemAction {
  type: FileUploadActionTypes.EnableEditFileItem
  fileItem: IFileUploadAttachment
}
interface IFileUploadProgressAction {
  type: FileUploadActionTypes.FileUploadProgress
  file: IFileUploadAttachment
  progress: number
}
interface IFileUploadRequestAction {
  type: FileUploadActionTypes.FileUploadRequest
  key: string
  currentFile: IFileUploadAttachment
  editing: boolean
}
export interface IFileUploadSuccessAction {
  type: FileUploadActionTypes.FileUploadSuccess
  attachment: any
  file: IFileUploadAttachment
}
interface IFileUploadFailureAction {
  type: FileUploadActionTypes.FileUploadFailure
  file: IFileUploadAttachment
  error: Error
}
interface ILoadExistingAttachmentsAction {
  type: FileUploadActionTypes.LoadExistingAttachments
  key: string
  attachments: List<IAttachment>
  append?: boolean
}
interface IRemoveFileUploadItemAction {
  type: FileUploadActionTypes.RemoveFileUploadItem
  key: string
  id: string
}
interface IToggleConfirmDeleteModalAction {
  type: FileUploadActionTypes.ToggleConfirmDeleteModal
  item: IFileUploadAttachment
}
interface IToggleFileUploadModalAction {
  type: FileUploadActionTypes.ToggleFileUploadModal
  key: string
  files: any
}
interface IUpdateFileMetadataAction {
  type: FileUploadActionTypes.UpdateFileMetadata
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | ICancelAllFilesAction
  | ICancelCurrentFileAction
  | IClearFileListAction
  | IEditFileDescriptionAction
  | IEditFileTitleAction
  | IEditMakePublicAction
  | IEditTagListAction
  | IEnableEditFileItemAction
  | IFileUploadRequestAction
  | IFileUploadProgressAction
  | IFileUploadSuccessAction
  | IFileUploadFailureAction
  | ILoadExistingAttachmentsAction
  | IRemoveFileUploadItemAction
  | IToggleConfirmDeleteModalAction
  | IToggleFileUploadModalAction

// ----------------
// ACTION CREATORS - These are functions exposed to UI components that will trigger a state transition.
// They don't directly mutate state, but they can have external side-effects (such as loading data).
function cancelAllFiles(): ICancelAllFilesAction {
  return { type: FileUploadActionTypes.CancelAllFiles }
}

function updateFileMetadata(): IUpdateFileMetadataAction {
  return {
    type: FileUploadActionTypes.UpdateFileMetadata,
  }
}

function uploadFailure(
  file: IFileUploadAttachment,
  error: Error,
): IFileUploadFailureAction {
  return {
    error,
    file,
    type: FileUploadActionTypes.FileUploadFailure,
  }
}

function uploadProgress(
  file: IFileUploadAttachment,
  progress: number,
): IFileUploadProgressAction {
  return {
    file,
    progress,
    type: FileUploadActionTypes.FileUploadProgress,
  }
}

function uploadSuccess(
  attachment: any,
  file: IFileUploadAttachment,
): IFileUploadSuccessAction {
  return {
    attachment,
    file,
    type: FileUploadActionTypes.FileUploadSuccess,
  }
}

export const actionCreators = {
  cancelAllFiles,
  cancelCurrentFile: (): ICancelCurrentFileAction => ({
    type: FileUploadActionTypes.CancelCurrentFile,
  }),
  clearFileList: (key: string): IClearFileListAction => ({
    key,
    type: FileUploadActionTypes.ClearFileList,
  }),
  editFileDescription: (description: string): IEditFileDescriptionAction => ({
    description,
    type: FileUploadActionTypes.EditFileDescription,
  }),
  editFileTitle: (title: string): IEditFileTitleAction => ({
    title,
    type: FileUploadActionTypes.EditFileTitle,
  }),
  editMakePublic: (value: boolean): IEditMakePublicAction => ({
    type: FileUploadActionTypes.EditMakePublic,
    value,
  }),
  editTagList: (tags: List<string>): IEditTagListAction => ({
    tags,
    type: FileUploadActionTypes.EditTagList,
  }),
  enableEditFileItem: (
    fileItem: IFileUploadAttachment,
  ): IEnableEditFileItemAction => ({
    fileItem,
    type: FileUploadActionTypes.EnableEditFileItem,
  }),
  loadExistingAttachments: (
    key: string,
    attachments: List<IAttachment>,
    append?: boolean,
  ): ILoadExistingAttachmentsAction => ({
    append,
    attachments,
    key,
    type: FileUploadActionTypes.LoadExistingAttachments,
  }),
  removeFileUploadItem: (
    key: string,
    id: string,
  ): IRemoveFileUploadItemAction => ({
    key,
    id,
    type: FileUploadActionTypes.RemoveFileUploadItem,
  }),
  requestFileUpload: (
    key: string,
    currentFile: IFileUploadAttachment,
    editing: boolean,
  ): IFileUploadRequestAction => ({
    currentFile,
    editing,
    key,
    type: FileUploadActionTypes.FileUploadRequest,
  }),
  toggleConfirmDeleteModal: (
    item: IFileUploadAttachment,
  ): IToggleConfirmDeleteModalAction => ({
    item,
    type: FileUploadActionTypes.ToggleConfirmDeleteModal,
  }),
  toggleFileUploadModal: (
    key: string,
    files: any,
  ): IToggleFileUploadModalAction => ({
    files,
    key,
    type: FileUploadActionTypes.ToggleFileUploadModal,
  }),
  updateFileMetadata,
}

// ----------------
// REDUCER - For a given state and action, returns the new state. To support time travel, this must not mutate the old state.
export const reducer: Reducer<FileUploadState> = (
  state: FileUploadState = new FileUploadState(),
  incomingAction: Action,
) => {
  const action = incomingAction as KnownAction

  switch (action.type) {
    case FileUploadActionTypes.CancelAllFiles:
      const file = state.currentFile

      return state
        .setIn(['files', file.key, file.id], file)
        .set('isInEditMode', false)
        .set('currentFile', undefined)
        .set('pendingFileCount', 0)
        .set('filesPendingUpload', List<FileUploadAttachment>())
        .set('isFileUploadModalOpen', { [file.key]: false }) as FileUploadState

    case FileUploadActionTypes.CancelCurrentFile:
      let currentFile = state.currentFile

      // Prevent deadlock if the currentFile is undefined
      if (typeof currentFile === 'undefined') {
        return state
          .set('isInEditMode', false)
          .set('filesPendingUpload', List<IFileUploadAttachment>())
          .set('pendingFileCount', 0) as FileUploadState
      }

      const filesPending = state.filesPendingUpload.filter((item: any) => {
        return item.file.name !== currentFile.file.name
      }) as List<FileUploadAttachment>

      if (filesPending && filesPending.size > 0) {
        currentFile = filesPending.first()

        return state
          .set('currentFile', currentFile)
          .set('pendingFileCount', filesPending.size)
          .set('filesPendingUpload', filesPending) as FileUploadState
      } else {
        return state
          .set('isInEditMode', false)
          .set('isFileUploadModalOpen', { [currentFile.key]: false })
          .set('currentFile', undefined)
          .set('filesPendingUpload', List<IFileUploadAttachment>())
          .set('pendingFileCount', 0) as FileUploadState
      }

    case FileUploadActionTypes.ClearFileList:
      return state.setIn(
        ['files', action.key],
        Map<string, FileUploadAttachment>(),
      ) as FileUploadState

    case FileUploadActionTypes.EditFileDescription:
      return state.setIn(
        ['currentFile', 'attachment', 'description'],
        action.description,
      ) as FileUploadState

    case FileUploadActionTypes.EditFileTitle:
      return state.setIn(
        ['currentFile', 'attachment', 'title'],
        action.title,
      ) as FileUploadState

    case FileUploadActionTypes.EditMakePublic:
      return state.setIn(
        ['currentFile', 'attachment', 'isPublic'],
        action.value,
      ) as FileUploadState

    case FileUploadActionTypes.EditTagList:
      return state.setIn(
        ['currentFile', 'attachment', 'tags'],
        action.tags,
      ) as FileUploadState

    case FileUploadActionTypes.EnableEditFileItem:
      const fileToEdit = state.getIn([
        'files',
        action.fileItem.key,
        action.fileItem.id,
      ]) as FileUploadAttachment

      if (fileToEdit) {
        const filePendingArray = List<FileUploadAttachment>()
        filePendingArray.insert(0, fileToEdit)

        return state
          .set('isInEditMode', true)
          .set('currentFile', fileToEdit)
          .set('filesPendingUpload', filePendingArray)
          .set('pendingFileCount', 1)
          .set('isFileUploadModalOpen', {
            [action.fileItem.key]: true,
          }) as FileUploadState
      } else {
        return state as FileUploadState
      }

    case FileUploadActionTypes.FileUploadRequest:
      const nextFiles = state.filesPendingUpload.filter((item: any) => {
        return item.file.name !== action.currentFile.file.name
      })

      if (state.isInEditMode) {
        if (
          state.hasIn(['files', action.currentFile.key]) &&
          state.hasIn(['files', action.key, action.currentFile.id])
        ) {
          return state
            .set('isInEditMode', false)
            .setIn(
              ['files', action.currentFile.key, action.currentFile.id],
              action.currentFile,
            )
            .set('isFileUploadModalOpen', { [action.currentFile.key]: false })
            .set('currentFile', undefined)
            .set('filesPendingUpload', List<FileUploadAttachment>())
            .set('pendingFileCount', 0) as FileUploadState
        } else {
          return state as FileUploadState
        }
      }

      if (nextFiles && nextFiles.size > 0) {
        return state
          .set('isInEditMode', false)
          .deleteIn(['filesPendingUpload', 0])
          .set('pendingFileCount', state.filesPendingUpload.size - 1)
          .set('currentFile', nextFiles.first())
          .setIn(
            ['files', nextFiles.first().key, action.currentFile.id],
            new FileUploadAttachment(action.currentFile),
          ) as FileUploadState
      } else {
        return state
          .set('isInEditMode', false)
          .setIn(
            ['files', action.currentFile.key, action.currentFile.id],
            new FileUploadAttachment(action.currentFile),
          )
          .set('isFileUploadModalOpen', { [action.currentFile.key]: false })
          .set('currentFile', undefined)
          .set('filesPendingUpload', List<FileUploadAttachment>())
          .set('pendingFileCount', 0) as FileUploadState
      }

    case FileUploadActionTypes.FileUploadProgress:
      if (state.hasIn(['files', action.file.key, action.file.id])) {
        return state.setIn(
          ['files', action.file.key, action.file.id, 'progress'],
          Math.min(99, Math.round(action.progress * 100)),
        ) as FileUploadState
      }

      return state as FileUploadState

    case FileUploadActionTypes.FileUploadSuccess:
      return state
        .setIn(['files', action.file.key, action.file.id, 'progress'], 100)
        .setIn(
          ['files', action.file.key, action.file.id, 'attachment'],
          action.attachment,
        ) as FileUploadState

    case FileUploadActionTypes.FileUploadFailure:
      return state
        .setIn(['files', action.file.key, action.file.id, 'progress'], 100)
        .setIn(
          ['files', action.file.key, action.file.id, 'error'],
          true,
        ) as FileUploadState

    case FileUploadActionTypes.LoadExistingAttachments:
      const filtered = action.attachments.filter((item: any) => {
        if (!item) {
          return false
        }

        return true
      })
      const existingAttachments = filtered.reduce(
        (lookup: any, item: any) =>
          lookup.set(
            item.get('id'),
            new FileUploadAttachment({
              attachment: item,
              file: {
                preview: item.getImageUrl(ImageSize.h50w50),
              },
              id: item.id,
              key: action.key,
            }),
          ),
        Map(),
      )

      if (action.append) {
        return state.mergeIn(
          ['files', action.key],
          state.getIn(['files', action.key]),
          existingAttachments,
        ) as FileUploadState
      }

      return state.setIn(
        ['files', action.key],
        existingAttachments,
      ) as FileUploadState

    case FileUploadActionTypes.ToggleConfirmDeleteModal:
      return state.setIn(
        ['files', action.item.key, action.item.id, 'isConfirmDeleteModalOpen'],
        !action.item.isConfirmDeleteModalOpen,
      ) as FileUploadState

    case FileUploadActionTypes.ToggleFileUploadModal:
      const pendingFileUploads = List(
        action.files.map((item: any, index: number) => {
          return new FileUploadAttachment({
            attachment: new Attachment({
              title: item.name,
            }),
            file: item,
            id: uuid(),
            key: action.key,
          })
        }),
      )

      return state
        .set('currentFile', pendingFileUploads.first())
        .set('filesPendingUpload', pendingFileUploads)
        .set('pendingFileCount', action.files.length)
        .set('isFileUploadModalOpen', {
          [action.key]: !state.isFileUploadModalOpen[action.key],
        }) as FileUploadState

    case FileUploadActionTypes.RemoveFileUploadItem:
      return state.deleteIn([
        'files',
        action.key,
        action.id,
      ]) as FileUploadState

    default:
      // The following line guarantees that every action in the KnownAction union has been covered by a case above
      const exhaustiveCheck: never = action
  }

  // For unrecognized actions (or in cases where actions have no effect), must return the existing state
  //  (or default initial state if none was supplied)
  return state
}

// -------------------------
// SAGAS
// -------------------------
export function* attemptUploadFileSaga(action: IFileUploadRequestAction) {
  if (action.editing) {
    const entity = action.currentFile.attachment

    const fileUploadOpts = {
      body: JSON.stringify(entity),
      method: RequestMethod.PUT,
    }

    const url = `/api/Files/${entity.id}`
    const result = yield makeAuthenticatedRequest(url, fileUploadOpts)
  } else {
    yield call(uploadFileSaga, action.currentFile, action.editing)
  }
}

function* updateFileMetadataSaga(action: IUpdateFileMetadataAction) {
  try {
    const fileState = yield select(selectModuleState)

    const baseUrl = `/api/files/${fileState.currentFile.attachment.id}`

    const opts = {
      body: JSON.stringify(fileState.currentFile.attachment.toJS()),
      method: RequestMethod.PUT,
    }

    const result = yield makeAuthenticatedRequest(baseUrl, opts)

    if (result.success === true) {
      yield put(
        successToast(
          'Success',
          `${fileState.currentFile.attachment.title} was successfully updated`,
        ),
      )
      yield put(cancelAllFiles())
    } else {
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(errorToast('Oops!', err.message))
  }
}

export function* uploadFileSaga(
  currentFile: IFileUploadAttachment,
  editing: boolean,
) {
  if (currentFile.file.size > MAX_FILE_UPLOAD_SIZE) {
    const message = `The file ${currentFile.file.name} is too large to upload`
    yield put(uploadFailure(currentFile, new Error(message)))
    yield put(errorToast('Oops!', message))
    return
  }

  const formData = new FormData()
  formData.append('fileName', currentFile.file.name)
  formData.append('mimeType', currentFile.file.type)
  formData.append('description', currentFile.attachment.description || '')
  formData.append('size', currentFile.file.size.toString())
  formData.append(
    'tags',
    currentFile.attachment.tags ? currentFile.attachment.tags.join(',') : '',
  )
  formData.append(
    'type',
    currentFile.file.type.indexOf('image/') > -1
      ? AttachmentType.Photo
      : AttachmentType.Document,
  )
  formData.append(
    'title',
    currentFile.attachment.title || currentFile.file.name,
  )
  formData.append(
    'isPublic',
    currentFile.attachment.isPublic ? 'true' : 'false',
  )
  formData.append('file', currentFile.file)

  const channel = yield call(createUploadFileChannel, '/api/Files', formData)

  while (true) {
    const channelResult = yield take(channel)

    if (channelResult.err) {
      yield put(uploadFailure(currentFile, channelResult.err))
      yield put(errorToast('Oops!', channelResult.err.message))
      return
    }

    if (channelResult.success) {
      if (channelResult.response) {
        yield put(
          uploadSuccess(
            Attachment.fromJS(channelResult.response.entity),
            currentFile,
          ),
        )
      }
      return
    }

    yield put(uploadProgress(currentFile, channelResult.progress))
  }
}

function* watchUpdateFileMetadata() {
  yield takeLatest(
    FileUploadActionTypes.UpdateFileMetadata,
    updateFileMetadataSaga,
  )
}

export function* watchUploadRequest() {
  yield takeEvery(
    FileUploadActionTypes.FileUploadRequest,
    attemptUploadFileSaga,
  )
}

export function* fileUploadRootSaga() {
  yield fork(watchUploadRequest)
  yield fork(watchUpdateFileMetadata)
}

// -------------------------------------------------
// SELECTORS
// -------------------------------------------------
export const selectModuleState = (state: ApplicationState): FileUploadState =>
  state.fileUpload

export const selectFiles = (identifier: string) =>
  createSelector(
    selectModuleState,
    (mState) => mState.files.get(identifier),
  )

export const selectAllState = createSelector(
  selectModuleState,
  (fileUpload) => ({
    currentFile: fileUpload.currentFile,
    files: fileUpload.files,
    filesPendingUpload: fileUpload.filesPendingUpload,
    isFileUploadModalOpen: fileUpload.isFileUploadModalOpen,
    isInEditMode: fileUpload.isInEditMode,
    pendingFileCount: fileUpload.pendingFileCount,
  }),
)
